import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { EditorState, Selection } from '@tiptap/pm/state';
import type { NodeViewConstructor } from '@tiptap/pm/view';
import { AnyExtension, Editor, JSONContent, createDocument } from '@tiptap/react';
import { ReactElement, createElement } from 'react';

import { nodeViewSet, resolveNodeViews } from './nodeViews';

/**
 * Static rendering of read-only rich text: the exact DOM a live editor would show, without one editor per
 * doc.
 *
 * Text and marks: ONE detached editor per extension set renders each doc in turn — the same schema,
 * plugins, decorations and view code as a live editor — and its DOM is copied. `DOMSerializer` alone is
 * not enough: plugins decorate at render time (`WrapContentInSpan`'s spans, `Placeholder`'s empty-block
 * class) and the view adds nodes (the `ProseMirror-trailingBreak` <br>). `staticRender.test.tsx` pins the
 * copy to a live editor's DOM.
 *
 * Node views are rendered by us, not captured from Tiptap: the detached editor draws each node-view node
 * as the same empty wrapper Tiptap's `ReactNodeView` draws (`staticNodeViewWrapper`), and the page mounts
 * the node type's `staticNodeView` component into it with the node and its extension's options as props
 * (`StaticNodeViewProps`, nodeViews.ts). Nothing depends on when or how Tiptap mounts React node views.
 */

export type StaticRichText = {
  html: string;
  // In document order, one per `.react-renderer` element in `html`.
  nodeViews: ReactElement[];
  // The parsed doc and the editor that drew `html`, to copy from the static doc as that editor copies.
  copySource: StaticCopySource;
};

export type StaticCopySource = {
  doc: ProseMirrorNode;
  editor: Editor;
};

type SharedRenderer = {
  editor: Editor;
  extensions: AnyExtension[];
};

/**
 * The wrapper Tiptap's `ReactNodeView` gives a node — `<span|div class="react-renderer node-{type}">`, plus
 * a content element the node view keeps outside the DOM unless the component renders `NodeViewContent` —
 * so ProseMirror treats the node exactly as it does in a live editor (e.g. it marks only leaf wrappers
 * `contenteditable="false"`). Pinned by the parity tests.
 */
const staticNodeViewWrapper: NodeViewConstructor = (node: ProseMirrorNode) => {
  const tag = node.isInline ? 'span' : 'div';
  const dom = document.createElement(tag);
  dom.className = `react-renderer node-${node.type.name}`;
  if (node.isLeaf) return { dom };
  const contentDOM = document.createElement(tag);
  contentDOM.dataset.nodeViewContentReact = '';
  contentDOM.dataset.nodeViewWrapper = '';
  contentDOM.style.whiteSpace = 'inherit';
  return { dom, contentDOM };
};

const renderersByExtras = new WeakMap<AnyExtension[], Map<string, SharedRenderer>>();

function sharedRenderer(extraExtensions: AnyExtension[], configKey: string, buildExtensions: () => AnyExtension[]) {
  let byConfig = renderersByExtras.get(extraExtensions);
  if (!byConfig) {
    byConfig = new Map();
    renderersByExtras.set(extraExtensions, byConfig);
  }
  const existing = byConfig.get(configKey);
  if (existing) return existing;

  const extensions = buildExtensions();
  const editor = new Editor({ element: document.createElement('div'), extensions, editable: false });
  // A doc with a live-only node view is never drawn here (resolveNodeViews), so every type gets the wrapper.
  editor.view.setProps({
    nodeViews: Object.fromEntries([...nodeViewSet(extensions).keys()].map(name => [name, staticNodeViewWrapper]))
  });
  const shared = { editor, extensions };
  byConfig.set(configKey, shared);
  return shared;
}

/**
 * Renders `doc` (undefined = the schema's default empty block) the way a fresh read-only editor with these
 * extensions would, or returns null when the doc holds a node view that has no `staticNodeView`.
 * `configKey` must identify everything `buildExtensions` depends on besides `extraExtensions`.
 */
export function renderRichTextStatically({
  buildExtensions,
  configKey,
  doc,
  extraExtensions,
  preserveWhitespace
}: {
  buildExtensions: () => AnyExtension[];
  configKey: string;
  doc: JSONContent | undefined;
  extraExtensions: AnyExtension[];
  preserveWhitespace?: true | 'full';
}): StaticRichText | null {
  const { editor, extensions } = sharedRenderer(extraExtensions, configKey, buildExtensions);

  const parsed = createDocument(doc ?? '', editor.schema, { preserveWhitespace });

  const resolution = resolveNodeViews(parsed, extensions);
  if (resolution.mode === 'live') return null;
  const nodeViews = resolution.nodeViews.map(({ component, extension, node }) =>
    createElement(component, { node, options: extension.options })
  );

  drawFresh(editor, parsed);
  const html = editor.view.dom.outerHTML;
  if (editor.view.dom.querySelectorAll('.react-renderer').length !== nodeViews.length) return null;
  return { html, nodeViews, copySource: { doc: parsed, editor } };
}

// A fresh state, exactly as a new editor starts: the doc parsed the way the editor parses its content
// (an empty doc gets the schema's default block), the caret at its start (Placeholder decorates the
// block holding it), the editor's own plugins. Set as a new state rather than through a transaction,
// because a transaction would run plugins' appendTransaction (TextDirection would add a `dir`) that a
// freshly created editor never runs.
export function drawFresh(editor: Editor, doc: ProseMirrorNode) {
  editor.view.updateState(
    EditorState.create({ doc, plugins: editor.state.plugins, selection: Selection.atStart(doc) })
  );
}

// Marks a static rich text's content element, the parent of its `.ProseMirror` root.
export const STATIC_RICH_TEXT_ATTRIBUTE = 'data-static-rich-text';
